import React from 'react';
import { observer } from 'mobx-react';
import { computed, observable } from 'mobx';
import ErrorBoundary from 'components/hoc/error_boundary.js';
import styled from '@emotion/styled';
import DeleteButton from 'components/button/delete_button';
import InPlaceText from 'components/form/in_place_text';
import InPlaceCheckbox from 'components/form/in_place_checkbox';
import { LanguageIcon } from '@seedlang/icons';
import Button from 'components/button/button';
import { noop, orderBy } from 'lodash';
import { AppUI } from '@seedlang/state';
import { Theme } from '@seedlang/constants';
import { isBlank, isPresent } from '@seedlang/utils';
import Alert from 'components/alert';
import InPlaceLanguageSelect from 'components/form/in_place_language_select';
import autobind from 'autobind-decorator';
import { WorksheetWordStore } from '@seedlang/stores';
import ReactTooltip from 'react-tooltip';
import InfoTooltip from 'components/info_tooltip';
import FlagButton from 'components/button/flag_button';
import Filter from 'components/backend_filter/filter';
import { Link } from 'react-router';
import ButtonPrimary from 'components/button/button_primary';
import Spinner from 'components/spinner';

const Wrapper = styled.div`
  margin: ${props => props.margin};
  overflow: auto;
  height: 600px;
  table {
    margin-top: 10px;
  }
`;

const LangWrapper = styled.div`
  display: flex;
`;

const Arrows = styled.div`
  display: flex;
  flex-direction: column;
  i {
    margin: 2px 0;
    color: #333;
    cursor: pointer;
  }
`;

const AddSource = styled.div`
  margin-top: 10px;
  display: flex;
  align-items: center;
`;

const Column = styled.div`
  width: ${props => props.width};
  margin-right: 10px;
`;

@observer
class WordSourceEditRoot extends React.Component {
  @observable displayType = 'english-not-rejected';
  @observable text;
  @observable languageId = 'EN';
  @observable filters = {};
  @observable filterInitialized = false;
  @observable isSubmitting = false;
  @observable isDeleting = false;

  // Create a local store object that mimics the store interface needed by Filter
  localStore = {
    filters: this.filters,
    filterInitialized: true,
    setFilter: (key, value, options = {}) => {
      this.filters[key] = value;
    },
    clearFilters: () => {
      this.filters = {};
    },
    getFilterValue: key => {
      return this.filters[key];
    },
    removeFilter: (key, options = {}) => {
      delete this.filters[key];
    },
    reloadIndexFiltered: () => {
      // No need to do anything since we're using local data
    },
    initializeFilters: defaultFilters => {
      this.filters = defaultFilters || {};
      this.filterInitialized = true;
    },
    totalCount: this.filteredTranslationSources?.length,
    requestCounter: 0,
  };

  constructor(props) {
    super(props);
    if (isPresent(props.languageId)) {
      this.languageId = props.languageId;
    }
    if (isPresent(props.worksheetWord)) {
      this.displayType = 'english-accepted';
    }
    if (isPresent(props.meaning)) {
      this.displayType = 'english-expanded';
    }
  }

  @computed get filteredTranslationSources() {
    let sources = this.props.word.translationSources;
    if (this.props.meaning) {
      sources = sources.filter(item => item.meaningId === this.props.meaning.id);
    } else {
      sources = sources.filter(item => isBlank(item.meaningId));
    }

    if (isPresent(this.filters.accepted)) {
      sources = sources.filter(item => item.accepted === (this.filters.accepted === 'true'));
    }
    if (isPresent(this.filters.rejected)) {
      sources = sources.filter(item => item.rejected === (this.filters.rejected === 'true'));
    }
    if (isPresent(this.filters['joins:source|language_id'])) {
      sources = sources.filter(item => item.source?.languageId === this.filters['joins:source|language_id']);
    }
    if (isPresent(this.filters.translated_by_ai)) {
      sources = sources.filter(item => item.translatedByAi === (this.filters.translated_by_ai === 'true'));
    }
    if (isPresent(this.filters.user_id)) {
      sources = sources.filter(item => isPresent(item.userId) === (this.filters.user_id === 'true'));
    }

    return this.sortSources(sources);
  }

  @computed get wordAttributes() {
    return [this.props.word.targetText, this.props.word.level && this.props.word.level.abbreviation, this.props.word.wordType && this.props.word.wordType.name].filter(item => item);
  }

  @computed get availableLanguageOptions() {
    const translationSources = this.props.word.translationSources;
    const languageCounts = translationSources.reduce((acc, source) => {
      const langId = source.source?.languageId;
      if (langId) {
        acc[langId] = (acc[langId] || 0) + 1;
      }
      return acc;
    }, {});

    const uniqueLanguageIds = Object.keys(languageCounts);

    return AppUI.languageOptionsForFilter
      .filter(option => uniqueLanguageIds.includes(option.id))
      .map(option => ({
        ...option,
        name: `${option.name} (${languageCounts[option.id]})`,
      }));
  }

  @computed get translationSources() {
    let sources;
    if (this.props.meaning) {
      sources = this.props.word.translationSources.filter(item => item.meaningId === this.props.meaning.id);
    } else {
      sources = this.props.word.translationSources.filter(item => isBlank(item.meaningId));
    }
    if (this.displayType === 'all') {
      return this.filteredTranslationSources;
    } else if (this.displayType === 'english-expanded') {
      sources = sources.filter(item => item.source && (item.source?.languageId === 'EN' || item.source?.languageId === 'U'));
    } else if (this.displayType === 'english-not-rejected') {
      sources = sources.filter(item => item.source && (item.source?.languageId === 'EN' || item.source?.languageId === 'U') && !item.rejected);
    } else if (this.displayType === 'english-accepted') {
      sources = sources.filter(item => item.source && (item.source?.languageId === 'EN' || item.source?.languageId === 'U') && !item.rejected && (!item.userId || item.accepted));
    }
    return this.sortSources(sources);
  }

  @autobind sortSources(sources) {
    return orderBy(sources, ['source.languageId', 'aggregateScore'], ['asc', 'desc']);
  }

  @computed get verbHasTranslationSourceWithoutTo() {
    return this.props.word.isVerb &&
      (this.displayType === 'all' ?
        (this.filteredTranslationSources.some(item => !item.source?.text.startsWith('to ')) && this.filters['joins:source|language_id']?.startsWith('EN')) :
        ((this.displayType === 'english-expanded' || this.displayType === 'english-not-rejected') && this.translationSources.some(item => !item.source?.text.startsWith('to ')))
      );
  }

  @autobind onSubmit() {
    this.isSubmitting = true;
    const data = {
      user_id: AppUI.user.id,
      force_accepted: true,
      translation_id: this.props.word.id,
      text: this.text,
      language_id: this.languageId,
      meaning_id: this.props.meaning?.id,
    };

    AppUI.translationSourceStore.create({data: data}, this.afterSubmit);
  }

  @autobind afterSubmit() {
    this.isSubmitting = false;
    this.text = null;
    this.props.onChange();
  }

  @autobind translationSourceHidden(translationSource) {
    return isPresent(this.props.worksheetWord.hiddenTranslationSourceIds) && this.props.worksheetWord.hiddenTranslationSourceIds.includes(translationSource.id);
  }

  @autobind toggleHideTranslationSource(translationSource) {
    if (isBlank(this.props.worksheetWord)) { }
    const translationSourceIdsArray = isPresent(this.props.worksheetWord.hiddenTranslationSourceIds) ? this.props.worksheetWord.hiddenTranslationSourceIds.split(',') : [];
    let newTranslationSourceIds;
    if (this.translationSourceHidden(translationSource)) {
      newTranslationSourceIds = translationSourceIdsArray.filter(item => item !== translationSource.id).join(',');
    } else {
      translationSourceIdsArray.push(translationSource.id);
      newTranslationSourceIds = translationSourceIdsArray.join(',');
    }
    this.props.worksheetWord.set('hiddenTranslationSourceIds', newTranslationSourceIds);
    WorksheetWordStore.update({ids: {worksheetWordId: this.props.worksheetWord.id}, data: {hiddenTranslationSourceIds: newTranslationSourceIds}}, noop);
  }

  @autobind flagTranslation(translation){
    translation.flaggedForRetranslation = !translation.flaggedForRetranslation;
    AppUI.translationSourceStore.update({ids: {translationSourceId: translation.id}, data: {flaggedForRetranslation: translation.flaggedForRetranslation}}, noop);
  }

  @autobind handleMeaningAssociation(translationSource) {
    const newMeaningId = translationSource.meaningId === this.props.meaning?.id ? null : this.props.meaning?.id;
    AppUI.translationSourceStore.update({
      ids: {translationSourceId: translationSource.id},
      data: {meaning_id: newMeaningId},
    }, this.props.onChange);
  }

  @autobind handleDelete(translationSourceId) {
    this.isDeleting = true;
    this.props.onRemove(translationSourceId);
    // Set a timeout to reset isDeleting after the deletion completes
    setTimeout(() => {
      this.isDeleting = false;
    }, 1000);
  }

  render() {
    return (
      <Wrapper margin={this.props.margin}>
        <h5>
          Vocab Trainer Source for {this.wordAttributes.join(' | ')}
          {this.props.meaning && ` - Meaning ${this.props.meaning.position}`}
        </h5>
        {
          isBlank(this.props.worksheetWord) &&
            <>
              <>
                {
                  isBlank(this.props.meaning) &&
                    <Button
                      fontSize='12px'
                      onClick={() => this.displayType = this.props.meaning ? 'English' : 'english-not-rejected'}
                      margin='10px 10px 0 0'
                      background={this.displayType === 'english-not-rejected' ? '#FFF' : Theme.blue}
                      color={this.displayType === 'english-not-rejected' ? Theme.blue : '#FFF'}
                      border={this.displayType === 'english-not-rejected' ? '2px solid #1DB5D7' : '2px solid #FFF'}
                    >
                      {this.props.meaning ? 'Show English' : 'Show English (Not Rejected)'}
                    </Button>
                }
                <Button
                  fontSize='12px'
                  onClick={() => this.displayType = 'english-expanded'}
                  margin='10px 10px 0 0'
                  background={this.displayType === 'english-expanded' ? '#FFF' : Theme.blue}
                  color={this.displayType === 'english-expanded' ? Theme.blue : '#FFF'}
                  border={this.displayType === 'english-expanded' ? '2px solid #1DB5D7' : '2px solid #FFF'}
                >
                  Show English (All)
                </Button>
                <Button
                  fontSize='12px'
                  onClick={() => this.displayType = 'all'}
                  margin='10px 10px 0 0'
                  background={this.displayType === 'all' ? '#FFF' : Theme.blue}
                  color={this.displayType === 'all' ? Theme.blue : '#FFF'}
                  border={this.displayType === 'all' ? '2px solid #1DB5D7' : '2px solid #FFF'}
                >
                  Show All
                </Button>
              </>
              {
                this.displayType === 'all' &&
                <Filter
                  store={this.localStore}
                  margin='10px 0 0 0'
                  namespace='translation_sources'
                  filters={[
                    {
                      type: 'boolean',
                      name: 'accepted',
                      label: 'Accepted',
                    },
                    {
                      type: 'boolean',
                      name: 'rejected',
                      label: 'Rejected',
                    },
                    {
                      type: 'select',
                      name: 'joins:source|language_id',
                      options: this.availableLanguageOptions,
                      labelField: 'name',
                      label: 'Language',
                    },
                    {
                      type: 'boolean',
                      name: 'translated_by_ai',
                      label: 'AI',
                    },
                    {
                      type: 'boolean',
                      name: 'user_id',
                      label: 'User',
                    },
                  ]}
                />
              }
            </>
        }
        <table className='table-wrapper admin-table'>
          <thead>
            <tr>
              <th width='50px' colSpan={2}>
                Score
              </th>
              <th>Source</th>
              <th>User</th>
              { isBlank(this.props.worksheetWord) && <th>Lang</th> }
              {
                (this.props.word.isNoun || this.props.word.isPronoun) &&
                  <th style={{display: 'flex'}}>Display gender
                    <InfoTooltip position='bottom'>
                      <div>Check this option if the source needs to specify the gender to be correct.</div>
                      <div style={{fontStyle: 'italic'}}>eg. die Lehrerin - teacher (f.)</div>
                      <div>This is mostly relevant for gendered nouns referring to people,</div>
                      <div>when translated to a language that doesn't have this nuance (eg. English).</div>
                    </InfoTooltip>
                  </th>
              }
              {
                isPresent(this.props.worksheetWord) &&
                <th>Hide</th>
              }
              {(AppUI.siteIsDefault || AppUI.user.developer) && <th width='30' />}
              <th width='30' />
            </tr>
          </thead>
          <tbody>
            {
              this.translationSources.map(item => {
                return (
                  <tr key={item.id}>
                    <td>
                      {item.aggregateScore}
                    </td>
                    <td>
                      <Arrows>
                        <i
                          className='fa fa-arrow-up'
                          onClick={() => this.props.onUpdateScore(item.id, item.score + 1)}
                          style={{color: item.score > 0 ? Theme.green : '#333'}}
                        />
                        <i
                          className='fa fa-arrow-down'
                          onClick={() => this.props.onUpdateScore(item.id, item.score - 1)}
                          style={{color: item.score < 0 ? Theme.red : '#333'}}
                        />
                      </Arrows>
                    </td>
                    <td
                      style={{paddingLeft: 10}}
                    >
                      {
                        (AppUI.siteIsDefault || AppUI.user.developer) &&
                          <Link
                            to={{name: 'builder.sources.edit', params: {sourceId: item.source.id}}}
                            target='_blank'
                            data-tip
                            data-for={`${item.id}-source`}
                          >
                          <i className='fa fa-chevron-right fa-block' style={{marginRight: '10px', fontSize: '12px'}}/>
                          <ReactTooltip
                            place='top'
                            type='dark'
                            effect='solid'
                            id={`${item.id}-source`}
                            class='custom-tooltip'
                          >
                            View & edit source
                            </ReactTooltip>
                          </Link>
                      }
                      {`${item.source.text}${item.source.displayGender ? this.props.word.genderAbbreviation : ''}`}
                      {
                        item.source?.languageId === 'EN' &&
                          <InPlaceText
                              placeholder='No British variant'
                              defaultValue={item.source.ukSpelling}
                              model='sources'
                              field='ukSpelling'
                              submitOnEnter={true}
                              rejectBlank={false}
                              id={item.source.id}
                              suffix={item.source && item.source.displayGender ? this.props.word.genderAbbreviation : ''}
                          />
                      }
                    </td>
                    <td>
                      {
                        isBlank(this.props.worksheetWord) && isBlank(this.props.meaning) &&
                          <>
                            <InPlaceCheckbox
                              iconStyle={{margin: '0 5px 0 0'}}
                              id={item.id}
                              value={item.accepted}
                              model='translation_sources'
                              field='accepted'
                              afterChange={this.props.onChange}
                              disabled={false}
                            />
                            <InPlaceCheckbox
                              icon='times'
                              iconStyle={{margin: '0 5px 0 0'}}
                              id={item.id}
                              value={item.rejected}
                              model='translation_sources'
                              field='rejected'
                              afterChange={this.props.onChange}
                              disabled={false}
                            />
                          </>
                      }
                      {
                        isPresent(item.userId) &&
                          <div style={{display: 'flex'}}>
                            {item.user.name}{isPresent(item.user.role) ? ` (${item.user.role})` : ''}
                          </div>
                      }
                      {
                        isBlank(item.userId) && item.translatedByAi &&
                        <div>AI</div>
                      }
                    </td>
                    {
                      isBlank(this.props.worksheetWord) &&
                        <td>
                          {
                            item.source && item.source?.languageId &&
                            <LangWrapper>
                              <LanguageIcon
                                languageId={item.source.languageId}
                                showTooltip
                                margin='0 5px 0 0'
                              />
                              {item.source.languageId}
                            </LangWrapper>
                          }
                        </td>
                    }
                    {
                      (this.props.word.isNoun || this.props.word.isPronoun) &&
                        <td>
                          <InPlaceCheckbox
                            iconStyle={{margin: '0 5px 0 0'}}
                            id={item.source.id}
                            value={item.source.displayGender}
                            model='sources'
                            field='display_gender'
                            afterChange={this.props.onChange}
                          />
                        </td>
                    }
                    {
                      isPresent(this.props.worksheetWord) &&
                      <td>
                        <span
                          data-tip
                          data-for={`${item.id}-hide`}
                        >
                          <InPlaceCheckbox
                            value={this.translationSourceHidden(item)}
                            onChange={() => this.toggleHideTranslationSource(item)}
                          />
                          <ReactTooltip
                            place='top'
                            type='dark'
                            effect='solid'
                            id={`${item.id}-hide`}
                            class='custom-tooltip'
                          >
                            Hide this source for this worksheet
                          </ReactTooltip>
                        </span>
                      </td>
                    }
                    {(AppUI.siteIsDefault || AppUI.user.developer) && (
                      <td>
                        <FlagButton
                          onConfirm={()=>this.flagTranslation(item)}
                          flagged={item.flaggedForRetranslation}
                        />
                      </td>
                    )}
                    <td>
                      {
                        (isBlank(this.props.worksheetWord) || item.user?.id === AppUI.user?.id || AppUI.user.admin) &&
                          <DeleteButton
                            onConfirm={() => this.handleDelete(item.id)}
                            message='Remove this source from this word?'
                            className='fa-block right'
                          />
                      }
                    </td>
                  </tr>
                );
              })
            }
          </tbody>
        </table>
        <AddSource>
          <Column width='300px'>
            <InPlaceText
              placeholder='Enter a new source'
              value={this.text}
              onChange={val => this.text = val}
              style={{ padding: '20px 10px' }}
              show
            />
          </Column>
          {
            isBlank(this.props.languageId) &&
              <Column
                width='150px'
              >
                <InPlaceLanguageSelect
                  onChange={val => this.languageId = val}
                  value={this.languageId}
                  showAbbreviation
                  showEdit
                />
              </Column>
          }
          <Column
            width='80px'
          >
            <ButtonPrimary onClick={this.onSubmit}>
              Submit
            </ButtonPrimary>
          </Column>
        </AddSource>
        {
          (this.isSubmitting || this.isDeleting) &&
          <div style={{ display: 'flex', justifyContent: 'center', margin: '10px 0' }}>
            <Spinner />
          </div>
        }
        {
          this.verbHasTranslationSourceWithoutTo && this.filters['joins:source|language_id']?.startsWith('EN') &&
            <Alert
              margin='5px 0 0 0'
              background={Theme.orange}
            >
              Please use "to " as the prefix for verb translations.
            </Alert>
        }
      </Wrapper>
    );
  }
}

export default ErrorBoundary(WordSourceEditRoot);
